/**
 * Terrain PixiJS Rendering
 *
 * Approach: Texture from pixel data for the fill (supports arbitrary destruction),
 * Graphics for edge glow.
 */

import {
  Container,
  Graphics,
  Sprite,
  Texture,
  type Renderer as PixiRendererType,
} from 'pixi.js';
import { TERRAIN, VISUAL, WORLD, Z, CIRCUIT, CRATER, CELL, MATERIAL } from '../constants';
import type { TerrainCrater } from '../types';
import type { StageTheme } from '../stages';
import { getActiveTheme, DEFAULT_STAGE_ID } from '../stages';
import { getGrid, findEdgeCells, rebuildColumnTopCacheRange, getGroundY, worldToGridY, getScorchAt, buildContourPolylines } from './grid';
import type { ContourCache } from './grid-rasterization';

const { CELL_SIZE } = TERRAIN;

// Module-level theme reference — updated by TerrainRenderer.setTheme()
let activeTheme: StageTheme = getActiveTheme(DEFAULT_STAGE_ID);

interface HotEdge {
  minGx: number; maxGx: number;
  minGy: number; maxGy: number;
  heat: number; // 1.0 → 0
}

/** Compute base depth-gradient RGB for a terrain cell (no scorch). */
function baseDepthRGB(gx: number, gy: number): [number, number, number] {
  const surfaceY = getGroundY(gx * CELL_SIZE);
  const surfaceGy = worldToGridY(surfaceY);
  const depth = gy - surfaceGy; // in grid cells

  // Pick palette based on cell material — reads from active stage theme
  const grid = getGrid();
  const isWire = grid !== null && grid[gy * TERRAIN.GRID_WIDTH + gx] === CELL.WIREFRAME;
  const surfaceRGB = isWire ? activeTheme.wireSurface : activeTheme.sandSurface;
  const standardRGB = isWire ? activeTheme.wireStandard : activeTheme.sandStandard;
  const midRGB = isWire ? activeTheme.wireMid : activeTheme.sandMid;
  const deepRGB = isWire ? activeTheme.wireDeep : activeTheme.sandDeep;

  if (depth <= 0) return surfaceRGB as unknown as [number, number, number];

  // Lerp between tiers
  if (depth < CIRCUIT.FILL_SURFACE_DEPTH) {
    const t = depth / CIRCUIT.FILL_SURFACE_DEPTH;
    return lerpRGB(surfaceRGB, standardRGB, t);
  }
  if (depth < CIRCUIT.FILL_MID_DEPTH) {
    const t = (depth - CIRCUIT.FILL_SURFACE_DEPTH) / (CIRCUIT.FILL_MID_DEPTH - CIRCUIT.FILL_SURFACE_DEPTH);
    return lerpRGB(standardRGB, midRGB, t);
  }
  if (depth < CIRCUIT.FILL_DEEP_DEPTH) {
    const t = (depth - CIRCUIT.FILL_MID_DEPTH) / (CIRCUIT.FILL_DEEP_DEPTH - CIRCUIT.FILL_MID_DEPTH);
    return lerpRGB(midRGB, deepRGB, t);
  }
  return deepRGB as unknown as [number, number, number];
}

/** Compute depth-gradient RGB with scorch blending. */
function depthRGB(gx: number, gy: number): [number, number, number] {
  const base = baseDepthRGB(gx, gy);
  const scorch = getScorchAt(gx, gy);
  if (scorch === 0) return base;
  const t = (scorch / 255) * CRATER.SCORCH_BLEND_MAX;
  return lerpRGB(base, CRATER.SCORCH_COLOR, t);
}

function lerpRGB(a: readonly number[], b: readonly number[], t: number): [number, number, number] {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}

/** Lerp between two hex colors (0xRRGGBB). */
function lerpColor(from: number, to: number, t: number): number {
  const fr = (from >> 16) & 0xff, fg = (from >> 8) & 0xff, fb = from & 0xff;
  const tr = (to >> 16) & 0xff, tg = (to >> 8) & 0xff, tb = to & 0xff;
  const r = Math.round(fr + (tr - fr) * t);
  const g = Math.round(fg + (tg - fg) * t);
  const b = Math.round(fb + (tb - fb) * t);
  return (r << 16) | (g << 8) | b;
}

export class TerrainRenderer {
  private worldContainer: Container;
  private renderer: PixiRendererType;

  private fillPixels: Uint8Array;
  private fillTexture: Texture | null = null;
  private fillSprite: Sprite | null = null;
  private edgeGfx: Graphics;
  private craterGfx: Graphics;
  private bgGrid: Graphics;
  private hotEdges: HotEdge[] = [];
  private craters: TerrainCrater[] = [];
  private edgeCache: [number, number][] | null = null;
  private contourCache: ContourCache | null = null;
  private contourDirty = true;
  private textureDirty = false;

  constructor(worldContainer: Container, renderer: PixiRendererType) {
    this.worldContainer = worldContainer;
    this.renderer = renderer;

    // Allocate RGBA pixel buffer for terrain fill (initial size — reallocated in buildFull if world height changes)
    this.fillPixels = new Uint8Array(TERRAIN.GRID_WIDTH * TERRAIN.GRID_HEIGHT * 4);

    // Edge glow graphics
    this.edgeGfx = new Graphics();
    this.edgeGfx.zIndex = Z.TERRAIN_EDGE;

    // Crater glow arcs (between fill and edge)
    this.craterGfx = new Graphics();
    this.craterGfx.zIndex = 1.6;

    // Background grid (hidden by default — stage_theme_change enables if showBackgroundGrid)
    this.bgGrid = new Graphics();
    this.bgGrid.zIndex = Z.BACKGROUND_GRID;
    this.bgGrid.visible = false;
    this.buildBackgroundGrid();

    worldContainer.addChild(this.bgGrid);
    worldContainer.addChild(this.craterGfx);
    worldContainer.addChild(this.edgeGfx);
  }

  /** Update the active stage theme — call before buildFull() on stage change. */
  setTheme(stageId: string): void {
    activeTheme = getActiveTheme(stageId);
  }

  setVisible(visible: boolean): void {
    if (this.fillSprite) this.fillSprite.visible = visible;
    this.edgeGfx.visible = visible;
    this.craterGfx.visible = visible;
    // bgGrid visibility is controlled by setGridVisible (stage theme), not here
  }

  /** Toggle background grid visibility per stage theme. */
  setGridVisible(visible: boolean): void {
    this.bgGrid.visible = visible;
  }

  /** Build the full terrain visuals from current grid state */
  buildFull(): void {
    // Reallocate pixel buffer if world height changed (e.g. ascent mode)
    const needed = TERRAIN.GRID_WIDTH * TERRAIN.GRID_HEIGHT * 4;
    if (this.fillPixels.length !== needed) {
      this.fillPixels = new Uint8Array(needed);
      // Force full texture recreation (old texture has wrong dimensions)
      if (this.fillSprite) { this.fillSprite.destroy(); this.fillSprite = null; }
      if (this.fillTexture) { this.fillTexture.destroy(true); this.fillTexture = null; }
    }
    this.buildFillTexture();
    this.edgeCache = findEdgeCells();
    this.contourCache = buildContourPolylines(this.edgeCache);
    this.contourDirty = false;
    this.redrawEdgeGlow();
    this.buildBackgroundGrid();
    this.craters = [];
    this.craterGfx.clear();
  }

  /** Update visuals after destruction at given grid bounds */
  updateAfterDestruction(bounds: { minGx: number; maxGx: number; minGy: number; maxGy: number }, skipHotEdge?: boolean): void {
    // Update column cache for affected range
    rebuildColumnTopCacheRange(bounds.minGx, bounds.maxGx);

    // Update fill pixels in dirty region
    const grid = getGrid();
    if (!grid) return;

    for (let gy = bounds.minGy; gy <= bounds.maxGy; gy++) {
      for (let gx = bounds.minGx; gx <= bounds.maxGx; gx++) {
        const idx = (gy * TERRAIN.GRID_WIDTH + gx) * 4;
        if (grid[gy * TERRAIN.GRID_WIDTH + gx] > 0) {
          const [r, g, b] = depthRGB(gx, gy);
          this.fillPixels[idx] = r;
          this.fillPixels[idx + 1] = g;
          this.fillPixels[idx + 2] = b;
          this.fillPixels[idx + 3] = 255;
        } else {
          this.fillPixels[idx + 3] = 0;
        }
      }
    }

    // Defer GPU texture upload — batched in updateHotEdges() once per render frame
    this.textureDirty = true;

    // Register hot edge region (skip for sand physics — settling shouldn't glow)
    if (!skipHotEdge) {
      this.hotEdges.push({
        minGx: bounds.minGx, maxGx: bounds.maxGx,
        minGy: bounds.minGy, maxGy: bounds.maxGy,
        heat: 1.0,
      });
      // Cap hot edge count
      while (this.hotEdges.length > CRATER.HOT_EDGE_MAX) {
        this.hotEdges.shift();
      }
    }

    // Incrementally update edge cache in dirty region
    this.updateEdgeCacheForBounds(bounds);
    this.redrawEdgeGlow();
  }

  private buildFillTexture(): void {
    const grid = getGrid();
    if (!grid) return;

    // Write RGBA pixel data from grid with depth-based color gradient
    for (let gy = 0; gy < TERRAIN.GRID_HEIGHT; gy++) {
      for (let gx = 0; gx < TERRAIN.GRID_WIDTH; gx++) {
        const i = gy * TERRAIN.GRID_WIDTH + gx;
        const idx = i * 4;
        if (grid[i] > 0) {
          const [r, g, b] = depthRGB(gx, gy);
          this.fillPixels[idx] = r;
          this.fillPixels[idx + 1] = g;
          this.fillPixels[idx + 2] = b;
          this.fillPixels[idx + 3] = 255;
        } else {
          this.fillPixels[idx] = 0;
          this.fillPixels[idx + 1] = 0;
          this.fillPixels[idx + 2] = 0;
          this.fillPixels[idx + 3] = 0;
        }
      }
    }

    this.rebuildFillTexture();
  }

  private rebuildFillTexture(): void {
    // Optimization: Reuse texture resource if it already exists
    if (this.fillTexture) {
      this.fillTexture.source.update();
      return;
    }

    // Create texture from pixel data (first run only)
    this.fillTexture = Texture.from({
      resource: this.fillPixels,
      width: TERRAIN.GRID_WIDTH,
      height: TERRAIN.GRID_HEIGHT,
    });

    this.fillSprite = new Sprite(this.fillTexture);
    this.fillSprite.scale.set(CELL_SIZE, CELL_SIZE);
    this.fillSprite.zIndex = Z.TERRAIN_FILL;
    this.worldContainer.addChild(this.fillSprite);
  }

  /** Incrementally update edge cache for a dirty region. */
  private updateEdgeCacheForBounds(bounds: { minGx: number; maxGx: number; minGy: number; maxGy: number }): void {
    // Cold start — full scan
    if (!this.edgeCache) {
      this.edgeCache = findEdgeCells();
      this.contourDirty = true;
      return;
    }

    // Expand bounds by 1 cell for neighbor checks (edge status of adjacent cells may change)
    const eMinGx = Math.max(0, bounds.minGx - 1);
    const eMaxGx = Math.min(TERRAIN.GRID_WIDTH - 1, bounds.maxGx + 1);
    const eMinGy = Math.max(0, bounds.minGy - 1);
    const eMaxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, bounds.maxGy + 1);

    // Remove old edges in expanded bounds
    this.edgeCache = this.edgeCache.filter(([gx, gy]) =>
      gx < eMinGx || gx > eMaxGx || gy < eMinGy || gy > eMaxGy
    );

    // Find new edges in expanded bounds and append
    const newEdges = findEdgeCells({ minGx: eMinGx, maxGx: eMaxGx, minGy: eMinGy, maxGy: eMaxGy });
    for (const edge of newEdges) {
      this.edgeCache.push(edge);
    }
    this.contourDirty = true;
  }

  /** Redraw edge glow from cached edge list using contour polyline strokes. */
  private redrawEdgeGlow(): void {
    const edges = this.edgeCache ?? findEdgeCells();

    // Rebuild contour polylines if dirty
    if (this.contourDirty || !this.contourCache) {
      this.contourCache = buildContourPolylines(edges);
      this.contourDirty = false;
    }

    const strokeWidth = CELL_SIZE + TERRAIN.EDGE_OVERDRAW * 2;
    this.edgeGfx.clear();

    // Draw normal sand contour polylines — all in one path with moveTo jumps
    if (this.contourCache.sand.length > 0) {
      for (const poly of this.contourCache.sand) {
        const p = poly.points;
        this.edgeGfx.moveTo(p[0], p[1]);
        for (let i = 2; i < p.length; i += 2) {
          this.edgeGfx.lineTo(p[i], p[i + 1]);
        }
      }
      this.edgeGfx.stroke({ color: activeTheme.sandEdgeColor, width: strokeWidth, alpha: TERRAIN.EDGE_ALPHA, join: 'round', cap: 'round' });
    }

    // Draw normal wireframe contour polylines
    if (this.contourCache.wire.length > 0) {
      for (const poly of this.contourCache.wire) {
        const p = poly.points;
        this.edgeGfx.moveTo(p[0], p[1]);
        for (let i = 2; i < p.length; i += 2) {
          this.edgeGfx.lineTo(p[i], p[i + 1]);
        }
      }
      this.edgeGfx.stroke({ color: activeTheme.wireEdgeColor, width: strokeWidth, alpha: TERRAIN.EDGE_ALPHA, join: 'round', cap: 'round' });
    }

    // Hot edge overdraw — only when hot edges are active
    if (this.hotEdges.length > 0) {
      this.drawHotEdgeOverdraw(strokeWidth);
    }
  }

  /** Overdraw hot edge segments with heat-lerped colors.
   *  For each polyline segment, check if its midpoint falls in a hot region.
   *  Group consecutive same-heat segments into sub-runs and stroke each. */
  private drawHotEdgeOverdraw(strokeWidth: number): void {
    if (!this.contourCache) return;

    // Process both sand and wire polylines
    const passes: { polylines: readonly { points: number[] }[]; baseColor: number; isWire: boolean }[] = [
      { polylines: this.contourCache.sand, baseColor: activeTheme.sandEdgeColor, isWire: false },
      { polylines: this.contourCache.wire, baseColor: activeTheme.wireEdgeColor, isWire: true },
    ];

    for (const pass of passes) {
      for (const poly of pass.polylines) {
        const p = poly.points;
        if (p.length < 4) continue;

        // For each segment, determine its heat bucket
        let runBucket = -1;
        let runStartIdx = 0;

        for (let i = 0; i < p.length - 2; i += 2) {
          // Segment midpoint in world coords → grid coords
          const midWx = (p[i] + p[i + 2]) * 0.5;
          const midWy = (p[i + 1] + p[i + 3]) * 0.5;
          const mgx = Math.floor(midWx / CELL_SIZE);
          const mgy = Math.floor(midWy / CELL_SIZE);

          // Find max heat at this grid position
          let maxHeat = 0;
          for (const he of this.hotEdges) {
            if (mgx >= he.minGx && mgx <= he.maxGx && mgy >= he.minGy && mgy <= he.maxGy) {
              maxHeat = Math.max(maxHeat, he.heat);
            }
          }

          const bucket = maxHeat > 0.05 ? Math.round(maxHeat * 10) : 0;

          if (bucket !== runBucket) {
            // Flush previous hot run
            if (runBucket > 0) {
              this.flushHotRun(p, runStartIdx, i, runBucket, strokeWidth, pass.baseColor, pass.isWire);
            }
            runBucket = bucket;
            runStartIdx = i;
          }
        }

        // Flush final run
        if (runBucket > 0) {
          this.flushHotRun(p, runStartIdx, p.length - 2, runBucket, strokeWidth, pass.baseColor, pass.isWire);
        }
      }
    }
  }

  /** Stroke a sub-run of a polyline with a heat-lerped color. */
  private flushHotRun(
    points: number[], startIdx: number, endIdx: number,
    bucket: number, strokeWidth: number,
    baseColor: number, isWire: boolean,
  ): void {
    const heat = bucket / 10;
    let color: number;
    let alpha: number;

    // Hot edges lerp from base alpha up toward ~0.85 at max heat
    const baseAlpha = TERRAIN.EDGE_ALPHA;
    if (isWire) {
      const sustained = Math.pow(heat, MATERIAL.WIREFRAME_REVEAL_SUSTAIN);
      color = lerpColor(activeTheme.wireEdgeColor, MATERIAL.WIREFRAME_REVEAL_COLOR, sustained);
      alpha = baseAlpha + (0.85 - baseAlpha) * heat;
    } else {
      color = lerpColor(activeTheme.sandEdgeColor, CRATER.HOT_EDGE_COLOR, heat);
      alpha = baseAlpha + (0.85 - baseAlpha) * heat;
    }

    this.edgeGfx.moveTo(points[startIdx], points[startIdx + 1]);
    for (let i = startIdx + 2; i <= endIdx + 1; i += 2) {
      this.edgeGfx.lineTo(points[i], points[i + 1]);
    }
    this.edgeGfx.stroke({ color, width: strokeWidth, alpha, join: 'round', cap: 'round' });
  }

  /** Register new crater glow at explosion site */
  addCrater(x: number, y: number, radius: number): void {
    this.craters.push({ x, y, radius, heat: 1.0 });
  }

  /** Decay hot edges + crater glow over time — call from variable update loop */
  updateHotEdges(dt: number): void {
    // Flush deferred texture upload (one GPU upload per render frame, not per crater)
    if (this.textureDirty) {
      this.rebuildFillTexture();
      this.textureDirty = false;
    }

    const decay = Math.pow(CRATER.HOT_EDGE_DECAY, dt * 60);
    for (let i = this.hotEdges.length - 1; i >= 0; i--) {
      this.hotEdges[i].heat *= decay;
      if (this.hotEdges[i].heat < 0.02) {
        this.hotEdges.splice(i, 1);
      }
    }
    // Redraw edge glow if any hot edges are still active (cache unchanged, only heat values change)
    if (this.hotEdges.length > 0) {
      this.redrawEdgeGlow();
    }

    // Crater glow decay + draw
    const craterDecay = Math.pow(CIRCUIT.CRATER_HEAT_DECAY, dt * 60);
    for (let i = this.craters.length - 1; i >= 0; i--) {
      this.craters[i].heat *= craterDecay;
      if (this.craters[i].heat < CIRCUIT.CRATER_REMOVE_THRESHOLD) {
        this.craters.splice(i, 1);
      }
    }
    this.drawCraters();
  }

  private drawCraters(): void {
    this.craterGfx.clear();

    const visible = this.craters
      .filter(c => c.heat > CIRCUIT.CRATER_RENDER_THRESHOLD)
      .sort((a, b) => b.heat - a.heat)
      .slice(0, CIRCUIT.CRATER_MAX_RENDERED);

    for (const crater of visible) {
      const color = crater.heat > 0.5 ? CIRCUIT.CRATER_HOT_COLOR : CIRCUIT.CRATER_COOL_COLOR;
      const width = CIRCUIT.CRATER_WIDTH_BASE + crater.heat * CIRCUIT.CRATER_WIDTH_HEAT;
      const alpha = crater.heat * CIRCUIT.CRATER_ALPHA_MULT;

      this.craterGfx.arc(crater.x, crater.y, crater.radius, Math.PI, 0);
      this.craterGfx.stroke({ width, color, alpha });
    }
  }

  private buildBackgroundGrid(): void {
    const spacing = VISUAL.GRID_SPACING / WORLD.SCALE; // Convert to virtual coords
    this.bgGrid.clear();

    // Vertical lines
    for (let x = 0; x <= WORLD.VIRTUAL_WIDTH; x += spacing) {
      this.bgGrid.moveTo(x, 0);
      this.bgGrid.lineTo(x, WORLD.VIRTUAL_HEIGHT);
    }
    // Horizontal lines
    for (let y = 0; y <= WORLD.VIRTUAL_HEIGHT; y += spacing) {
      this.bgGrid.moveTo(0, y);
      this.bgGrid.lineTo(WORLD.VIRTUAL_WIDTH, y);
    }
    this.bgGrid.stroke({ width: 1, color: VISUAL.GRID_COLOR, alpha: 0.5 });
  }

  destroy(): void {
    if (this.fillSprite) this.fillSprite.destroy();
    if (this.fillTexture) this.fillTexture.destroy(true);
    this.edgeGfx.destroy();
    this.craterGfx.destroy();
    this.bgGrid.destroy();
  }
}
